import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { STEPPER_PARENT, StepperParent } from './stepper.token';

/**
 * A single step inside `<app-stepper>`. Reads its own index and active state from the
 * parent stepper via injection and toggles its own host `[hidden]` binding, so only
 * the active step's projected content is visible.
 *
 * @feature shared
 */
@Component({
  selector: 'app-stepper-step',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
    '[hidden]': '!isActive()',
  },
  template: `
    <ng-content />
  `,
})
export class StepperStepComponent {
  private readonly parent = inject<StepperParent>(STEPPER_PARENT, { optional: true });

  /** Visible label used by the parent to build the trail. */
  readonly label = input.required<string>();

  /** Optional short helper text. */
  readonly hint = input<string | undefined>(undefined);

  /** Mark the step as errored; parent renders it in danger color. */
  readonly hasError = input<boolean>(false);

  /** Index of this step within its parent. */
  readonly index = computed(() => {
    const steps = this.parent?.steps();
    if (!steps) return -1;
    return steps.findIndex(s => s === this);
  });

  /** True when this step is the active one. */
  readonly isActive = computed(() => this.parent?.activeIndex() === this.index());
}
